/**
 * Advanced particle system types and utilities
 * Provides flexible ValueOrRange and LifetimeValue systems for maximum particle control
 */

/**
 * Describes a value that can be either a constant or a range for random selection.
 * @example
 * let life: ValueOrRange<number> = 5; // Always 5
 * let life: ValueOrRange<number> = [3, 7]; // Random number from 3 to 7
 */
export type ValueOrRange<T> = T | [T, T];

/**
 * Easing function that takes progress (0 to 1) and returns modified progress.
 */
export type EasingFunction = (t: number) => number;

/**
 * Describes a property that interpolates over the particle's lifetime.
 * @template T - Value type (e.g., number for size or color).
 */
export interface LifetimeValue<T> {
  /** Initial value. Can be a range for randomization. */
  start: ValueOrRange<T>;
  /** Final value. Can be a range for randomization. */
  end: ValueOrRange<T>;
  /**
   * Easing function for controlling interpolation curve.
   * Defaults to linear interpolation.
   */
  ease?: EasingFunction;
}

/**
 * Collection of standard Easing functions for adding "juice" to animations.
 * @see https://easings.net/
 */
export const Easing = {
  linear: (t: number) => t,
  inQuad: (t: number) => t * t,
  outQuad: (t: number) => t * (2 - t),
  inOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  inCubic: (t: number) => t * t * t,
  outCubic: (t: number) => --t * t * t + 1,
  inOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
  inQuart: (t: number) => t * t * t * t,
  outQuart: (t: number) => 1 - --t * t * t * t,
  inOutQuart: (t: number) => (t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t),
  inSine: (t: number) => 1 - Math.cos((t * Math.PI) / 2),
  outSine: (t: number) => Math.sin((t * Math.PI) / 2),
  inOutSine: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
  inExpo: (t: number) => (t === 0 ? 0 : Math.pow(2, 10 * (t - 1))),
  outExpo: (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  outBounce: (t: number) => {
    const n1 = 7.5625;
    const d1 = 2.75;
    if (t < 1 / d1) {
      return n1 * t * t;
    }
    if (t < 2 / d1) {
      return n1 * (t -= 1.5 / d1) * t + 0.75;
    }
    if (t < 2.5 / d1) {
      return n1 * (t -= 2.25 / d1) * t + 0.9375;
    }
    return n1 * (t -= 2.625 / d1) * t + 0.984375;
  },
  // Additional juicy easing functions
  inBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return c3 * t * t * t - c1 * t * t;
  },
  outBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  inOutBack: (t: number) => {
    const c1 = 1.70158;
    const c2 = c1 * 1.525;
    return t < 0.5
      ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
      : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
  },
  inElastic: (t: number) => {
    const c4 = (2 * Math.PI) / 3;
    return t === 0 ? 0 : t === 1 ? 1 : -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * c4);
  },
  outElastic: (t: number) => {
    const c4 = (2 * Math.PI) / 3;
    return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  },
  inOutElastic: (t: number) => {
    const c5 = (2 * Math.PI) / 4.5;
    return t === 0
      ? 0
      : t === 1
        ? 1
        : t < 0.5
          ? -(Math.pow(2, 20 * t - 10) * Math.sin((20 * t - 11.125) * c5)) / 2
          : (Math.pow(2, -20 * t + 10) * Math.sin((20 * t - 11.125) * c5)) / 2 + 1;
  },
};

/**
 * Utility function to resolve a concrete value from ValueOrRange.
 * Optimized for numbers, handles arrays for non-numeric types.
 */
export function resolveValue<T>(valueOrRange: ValueOrRange<T>): T {
  if (Array.isArray(valueOrRange)) {
    if (typeof valueOrRange[0] === 'number' && typeof valueOrRange[1] === 'number') {
      // Optimization for numbers
      return (valueOrRange[0] + Math.random() * (valueOrRange[1] - valueOrRange[0])) as T;
    }
    // For non-numeric arrays (e.g., textures or colors) select random element
    return valueOrRange[Math.floor(Math.random() * valueOrRange.length)];
  }
  return valueOrRange;
}

/**
 * Interpolates between start and end values using easing function.
 * @param start - Start value
 * @param end - End value
 * @param progress - Progress from 0 to 1
 * @param ease - Easing function (defaults to linear)
 * @returns Interpolated value
 */
export function interpolateValue<T extends number>(
  start: T,
  end: T,
  progress: number,
  ease: EasingFunction = Easing.linear
): T {
  const easedProgress = ease(progress);
  return (start + (end - start) * easedProgress) as T;
}

/**
 * Interpolates color values (supports both single colors and arrays).
 * @param start - Start color(s)
 * @param end - End color(s)
 * @param progress - Progress from 0 to 1
 * @param ease - Easing function
 * @returns Interpolated color
 */
export function interpolateColor(
  start: number | number[],
  end: number | number[],
  progress: number,
  ease: EasingFunction = Easing.linear
): number {
  // For arrays, we should pick one color at initialization, not every frame
  // This prevents flickering during animation
  const startColor = Array.isArray(start) ? start[0] : start;
  const endColor = Array.isArray(end) ? end[0] : end;
  return interpolateValue(startColor, endColor, progress, ease);
}
